import React, { useState, useEffect, useMemo } from "react"
import { Grid, Box, useMediaQuery, useTheme, Collapse, Hidden, makeStyles } from "@material-ui/core"
import moment from "moment"
import { useHistory, useParams } from "react-router-dom"
import Config from "react-global-configuration"
import {
  Content,
  AreaHeader,
  FormatDateFormal,
  UnconfirmedActionCard,
  JobsCompletionChartCard,
  JobsGroupsCompletionChartCards,
  Activity,
  DashboardModule,
  RequirePermissions,
  InsufficientPermissionsMessage,
  MoreJobsCount,
  DesktopCreateJobButton,
  MobileCreateJobButton,
  JobCreator,
  IconMessage,
  DeviceGroupsDisplay,
  TrainingLocationCompletionCard,
  JobList,
  TrainingAccreditationDashboardModule,
} from "../../components"
import {
  useQueryNotifications,
  useLazyQueryJobs,
  NOTIFICATION_TYPES,
  makeDashboardJobListFilters,
  useQueryTrainingCoursesCount,
} from "../../data"
import { useAuth } from "../../services"
import { ScrollToTop } from "../../components/Scrolling/ScrollToTop"
import { toId, useDateUtils } from "../../utils"
import { UnresolvedActionActionCard } from "../../components/ActionCards/UnresolvedActionActionCard"

const useStyles = makeStyles(() => ({
  gridItem: {
    width: "100%",
  },
}))

const Dashboard = () => {
  const { jobsMs } = Config.get("polling")
  const { type } = useParams()
  const classes = useStyles()
  const history = useHistory()
  const { momentToScheduleDate } = useDateUtils()
  const theme = useTheme()
  const xs = useMediaQuery(theme.breakpoints.down("xs"))
  const {
    location,
    principal: { userID },
    settings: { deviceGroups },
    hasPermission,
    hasFeature,
    permissionGroups,
  } = useAuth()
  const date = moment()
    .tz(location?.timeZone || moment.tz.guess())
    .startOf("day")

  const [unsubscribes, setUnsubscribes] = useState(null)
  const [loadJobs, jobsResult] = useLazyQueryJobs()
  const { data: trainingCoursesCountData } = useQueryTrainingCoursesCount()
  const { called, data: dataJobs, refetch, subscribe, startPolling, stopPolling } = jobsResult
  const [locationId, setLocationId] = useState(null)
  const totalJobs = dataJobs && dataJobs.jobs.total

  const [notificationUnsubscribes, setNotificationUnsubscribes] = useState(null)

  const {
    called: calledNotifications,
    data: dataNotifications,
    subscribe: subscribeNotifications,
  } = useQueryNotifications()

  useEffect(() => {
    if (calledNotifications && !notificationUnsubscribes) {
      setNotificationUnsubscribes(subscribeNotifications())
      return () => {
        if (notificationUnsubscribes) {
          notificationUnsubscribes.map((unsub) => unsub())
        }
      }
    }
  }, [calledNotifications, notificationUnsubscribes, subscribeNotifications])

  const attentionNotifications = dataNotifications?.notifications.items.filter(
    ({ hasConfirm, confirmed, action }) => (hasConfirm && !confirmed) || (action && !action.resolvedAt)
  )
  const hasUnconfirmed = Boolean(attentionNotifications?.length)

  const title = <FormatDateFormal value={date} />

  const jobListFilters = useMemo(() => makeDashboardJobListFilters({ userID }), [userID])

  useEffect(() => {
    if (location && location.id && locationId !== location.id && hasPermission(permissionGroups.readJobs)) {
      if (stopPolling) stopPolling()
      const newLocationId = location.id
      setLocationId(newLocationId)
      if (called && refetch)
        refetch({ location: newLocationId, date: momentToScheduleDate(date), groups: deviceGroups })
      else loadJobs({ variables: { location: newLocationId, date: momentToScheduleDate(date), groups: deviceGroups } })
    }
  }, [
    called,
    date,
    deviceGroups,
    hasPermission,
    loadJobs,
    location,
    locationId,
    momentToScheduleDate,
    permissionGroups.readJobs,
    refetch,
    stopPolling,
  ])

  useEffect(() => {
    if (startPolling && jobsMs) startPolling(jobsMs)
  }, [jobsMs, startPolling, locationId])

  useEffect(() => {
    if (called && !unsubscribes) {
      setUnsubscribes(subscribe())
      return () => {
        if (unsubscribes) {
          unsubscribes.map((unsub) => unsub())
        }
      }
    }
  }, [called, subscribe, unsubscribes, setUnsubscribes])

  const subTitle = hasUnconfirmed ? (
    "There are items requiring your attention"
  ) : deviceGroups ? (
    <DeviceGroupsDisplay />
  ) : location ? (
    location.name
  ) : (
    "My dashboard"
  )

  const baseUri = "/dashboard"

  const handleCreate = (createType) => {
    history.push(`${baseUri}/${createType}/new`)
  }
  const handleCreateClose = () => {
    history.push(baseUri)
  }

  const hasTraining = hasFeature("training")
  const hasTrainingCourses = Boolean(trainingCoursesCountData?.trainingCoursesCount)
  const hasJobsItems = Boolean(dataJobs?.jobs.items.length)
  const hasLocationOverview = hasPermission(["activity_read_daily_all", "training_read_locations"])

  const modifiedAt1 = new Date()
  modifiedAt1.setMinutes(modifiedAt1.getMinutes() - 4)

  const modifiedAt2 = new Date()
  modifiedAt2.setMinutes(modifiedAt2.getMinutes() - 65)

  const isOpen = Boolean(type)

  return (
    <>
      <ScrollToTop />
      <JobCreator open={Boolean(type) && type} onClose={handleCreateClose} />

      {(!xs || (xs && !isOpen)) && (
        <>
          <AreaHeader
            title={title}
            subTitle={subTitle}
            desktopButtons={<DesktopCreateJobButton onCreateClick={handleCreate} />}
            mobileButtons={<MobileCreateJobButton onCreateClick={handleCreate} />}
            hasUnconfirmed={hasUnconfirmed}
          />

          <Content full split={!xs} mb={0}>
            <Grid container direction="row" spacing={xs ? 0 : 6}>
              <Grid item xs={12} sm={6}>
                <Grid>
                  <Hidden smUp>
                    <TrainingAccreditationDashboardModule />
                  </Hidden>

                  <Collapse in={hasUnconfirmed}>
                    {hasUnconfirmed && (
                      <DashboardModule title={{ text: "Attention items", unconfirmed: true }}>
                        {attentionNotifications.map((notification) => (
                          <Box key={toId(notification)}>
                            {notification.type !== NOTIFICATION_TYPES.ACTION && (
                              <UnconfirmedActionCard notification={notification} />
                            )}
                            {notification.type === NOTIFICATION_TYPES.ACTION && (
                              <UnresolvedActionActionCard notification={notification} />
                            )}
                          </Box>
                        ))}
                      </DashboardModule>
                    )}
                  </Collapse>

                  <DashboardModule
                    title={{
                      text: `Today's Jobs${totalJobs ? ` (${totalJobs})` : ""}`,
                      unconfirmed: false,
                    }}
                    cy="DashboardModule-jobs"
                  >
                    <RequirePermissions requires={permissionGroups.readJobs} lacks={<InsufficientPermissionsMessage />}>
                      <Box mb={4}>
                        <JobList
                          {...jobsResult}
                          filters={jobListFilters}
                          date={date}
                          compact
                          empty={
                            <IconMessage
                              name="empty-assignedjobs"
                              text="You don't have any jobs assigned to you right now."
                            />
                          }
                          showEndMessage={false}
                          showShowMore={xs}
                          pageSize={12}
                        />
                      </Box>
                      <MoreJobsCount jobs={jobsResult.data && jobsResult.data.jobs} />
                    </RequirePermissions>
                  </DashboardModule>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid>
                  <Hidden xsDown>
                    <TrainingAccreditationDashboardModule />
                  </Hidden>

                  {hasLocationOverview && (hasJobsItems || (hasTraining && hasTrainingCourses)) && (
                    <DashboardModule title={{ text: "Location Overview", unconfirmed: false }}>
                      <Grid container spacing={2} layout="column">
                        {hasJobsItems && (
                          <RequirePermissions requires={["activity_read_daily_all"]}>
                            <Grid item lg={hasTraining && hasTrainingCourses ? 6 : 12} className={classes.gridItem}>
                              <JobsCompletionChartCard
                                jobs={dataJobs && dataJobs.jobs.items}
                                title={<strong>Today's Jobs</strong>}
                                detail={({ overall }) => (
                                  <>
                                    Staff have completed{" "}
                                    <strong>
                                      {overall.completed}/{overall.total}
                                    </strong>{" "}
                                    jobs
                                  </>
                                )}
                                adminOnly
                              />
                            </Grid>
                          </RequirePermissions>
                        )}

                        {hasTrainingCourses && (
                          <RequirePermissions requires={["training_read_locations"]} features={["training"]}>
                            <Grid item lg={hasJobsItems ? 6 : 12} className={classes.gridItem}>
                              <TrainingLocationCompletionCard location={location} />
                            </Grid>
                          </RequirePermissions>
                        )}
                      </Grid>
                    </DashboardModule>
                  )}

                  {(Boolean(dataJobs?.jobs.items.some((item) => item.groups.length)) ||
                    !dataJobs?.jobs.items.length) && (
                    <RequirePermissions requires={["activity_read_daily_groups", "activity_read_daily_all"]}>
                      <DashboardModule title={{ text: "Daily Progress", unconfirmed: false }}>
                        {dataJobs && dataJobs.jobs.items.length === 0 && (
                          <IconMessage
                            name="empty-activity"
                            text="There's no daily activity to report at the moment."
                          />
                        )}

                        <RequirePermissions requires={["activity_read_daily_groups"]}>
                          <JobsGroupsCompletionChartCards jobs={dataJobs && dataJobs.jobs.items} />
                        </RequirePermissions>
                      </DashboardModule>
                    </RequirePermissions>
                  )}

                  <Box mb={5}>
                    <Activity jobs={dataJobs && dataJobs.jobs.items} />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Content>
        </>
      )}
    </>
  )
}

export default Dashboard
